import {
  useWSSnackbar,
  WSElement,
  WSEmptyState,
  WSGrid,
  WSList,
  WSPage,
  WSPageToolbar,
  WSPageToolbarAction
} from "@wingspanhq/fe-component-library";
import React, { useCallback, useMemo } from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";

import { useUserId } from "../../../../query/hooks/helpers";
import { useQueryPayerEngagement } from "../../../../query/payerEngagements/queries/useQueryPayerEngagement";
import { useQueryPayer } from "../../../../query/payers/queries/useQueryPayer";
import { useQueryPayerRows } from "../../../../query/search/payer/queries/useQueryPayerRows";
import { useQueryEngagementsWorkDefinitions } from "../../../../query/workDefinition/queries/useQueryEngagementWorkDefinitions";
import { useQueryWorkItems } from "../../../../query/workItem/queries/useQueryWorkItems";
import { useMutationCancelEditingRequest } from "../../../../query/workLog/mutations/useMutationCancelEditingRequest";
import { useQueryWorkLog } from "../../../../query/workLog/queries/useQueryWorkLog";
import { useQueryWorkLogConfigForPayee } from "../../../../query/workLogConfig/queries/useQueryWorkLogConfigForPayee";
import {
  useModalWorkLogHistory,
  WorkLogHistoryWidget
} from "../../components/WorkLogHistoryWidget";
import { useBrowserPageTitle } from "../../../../shared/hooks/useBrowserPageTitle";
import { getPayerName } from "../../../Payers/utils/getPayerNames";
import { AlertWorkLogReviewStatusPayee } from "../../components/AlertWorkLogReviewStatusPayee";
import { CardWorkItems } from "../../components/CardWorkItems";
import { WorkLogAdditionalDetailsEditable } from "../../components/WorkLogAdditionalDetailsEditable";
import { WorkLogAssignmentInfoPayeeView } from "../../components/WorkLogAssignmentInfoPayeeView";
import { WorkLogStatusStepperPayeeView } from "../../components/WorkLogStatusStepperPayeeView";
import { useModalDeleteWorkLog } from "../../modals/ModalDeleteWorkLog";
import { useModalRequestEditing } from "../../modals/ModalRequestEditing";
import { useModalSubmitForReview } from "../../modals/ModalSubmitForReview";
import { PATH_WORK_LOGS_PAYEE } from "../../paths";
import {
  canPayeeDelete,
  canPayeeEdit,
  canPayeeRequestEditing,
  canPayeeSubmit,
  hasPayeeRequestedEditing,
  isStatusApproved
} from "../../utils/workLogPermissions";

export const RoutePayeeWorkLogDetails: React.FC<
  RouteComponentProps<{ workLogId: string }>
> = props => {
  const workLogId = props.match.params.workLogId;
  const userId = useUserId();
  const history = useHistory();

  // Query work log
  const queryWorkLog = useQueryWorkLog(workLogId);
  const workLog = queryWorkLog.data;

  // Query payer information
  const queryPayerRow = useQueryPayerRows(
    {
      filter: {
        "engagements.payerPayeeEngagementId": workLog?.payerPayeeEngagementId
      }
    },
    {
      enabled: !!workLog?.payerPayeeEngagementId
    }
  );

  const payerId = queryPayerRow.data?.[0]?.payerId;
  const payerPayeeEngagementId = workLog?.payerPayeeEngagementId;

  const queryPayer = useQueryPayer(payerId!, {
    enabled: !!payerId
  });

  const queryPayerPayeeEngagement = useQueryPayerEngagement(
    payerId!,
    payerPayeeEngagementId!,
    {
      enabled: !!payerId && !!payerPayeeEngagementId
    }
  );

  const payer = queryPayer.data;
  const payerPayeeEngagement = queryPayerPayeeEngagement.data;

  // Query work definitions
  const queryWorkDefinitions = useQueryEngagementsWorkDefinitions(
    {
      payerId: payerId!,
      engagementId: payerPayeeEngagementId!
    },
    {
      enabled: !!(payerId && payerPayeeEngagementId)
    }
  );

  // Query payer's work log config (controls whether attachments are required on submission)
  const queryWorkLogConfigForPayee = useQueryWorkLogConfigForPayee(
    payerId as string,
    {
      enabled: !!payerId
    }
  );

  // Query work items
  const queryWorkItems = useQueryWorkItems({
    "filter[workLogId][=]": workLogId
  });

  // Modals and mutations for payee actions
  const modalSubmitForReview = useModalSubmitForReview();
  const modalDeleteWorkLog = useModalDeleteWorkLog();
  const modalRequestEditing = useModalRequestEditing();
  const modalWorkLogHistory = useModalWorkLogHistory();
  const { openSnackbar } = useWSSnackbar();
  const [cancelEditing] = useMutationCancelEditingRequest();

  // Browser page title
  const browserTitle = useMemo(() => {
    if (!payerPayeeEngagementId || !payer) {
      return "Work Log Details";
    }

    const payerName = getPayerName(payer);
    return payerPayeeEngagement
      ? `Work Log – ${payerName} • ${payerPayeeEngagement.engagementName}`
      : `Work Log – ${payerName}`;
  }, [payer, payerPayeeEngagement, payerPayeeEngagementId]);

  useBrowserPageTitle(browserTitle);

  // Derived state for work log status
  const status = workLog?.status;
  const canEdit = canPayeeEdit(status);
  const canSubmit = canPayeeSubmit(status);
  const canDelete = canPayeeDelete(status);
  const canRequestEditing = canPayeeRequestEditing(status);
  const editingRequested = hasPayeeRequestedEditing(workLog as any);
  const isApproved = isStatusApproved(status);

  const attachmentsRequired =
    queryWorkLogConfigForPayee.data?.requireAttachments?.enabled === true;
  const attachmentsMissing =
    attachmentsRequired && (workLog?.attachmentIds?.length ?? 0) === 0;

  // Other party name for per-item "Last edited by" pill
  const payerName = payer ? getPayerName(payer) : undefined;

  // Action handlers
  const handleViewInvoice = useCallback(() => {
    history.push(`/member/invoices/${workLog?.invoiceId}`);
  }, [workLog?.invoiceId, history]);

  const handleSubmitForReview = useCallback(() => {
    if (workLog) {
      modalSubmitForReview.open({
        workLog,
        onClose: result => {
          if (result?.success) {
            queryWorkLog.refetch();
          }
        }
      });
    }
  }, [workLog, modalSubmitForReview, queryWorkLog]);

  const handleDeleteWorkLog = useCallback(() => {
    if (workLog) {
      modalDeleteWorkLog.open({
        workLog,
        onClose: result => {
          if (result?.success) {
            history.push(PATH_WORK_LOGS_PAYEE);
          }
        }
      });
    }
  }, [workLog, modalDeleteWorkLog, history]);

  const handleRequestEditing = useCallback(() => {
    if (workLog) {
      const clientName = payer ? getPayerName(payer) : undefined;
      modalRequestEditing.open({
        workLog,
        clientName,
        onClose: result => {
          if (result?.success) {
            queryWorkLog.refetch();
          }
        }
      });
    }
  }, [workLog, payer, modalRequestEditing, queryWorkLog]);

  const handleCancelEditingRequest = useCallback(() => {
    if (workLog) {
      cancelEditing(
        { workLogId: workLog.workLogId },
        {
          onSuccess: () => {
            openSnackbar({
              icon: { name: "check" },
              message: "Editing request cancelled"
            });
            queryWorkLog.refetch();
          },
          onError: () => {
            openSnackbar({
              type: "warning",
              message: "Failed to cancel editing request. Please try again."
            });
          }
        }
      );
    }
  }, [workLog, cancelEditing, openSnackbar, queryWorkLog]);

  const handleViewHistory = useCallback(() => {
    modalWorkLogHistory.open({ workLogId });
  }, [modalWorkLogHistory, workLogId]);

  // Build breadcrumbs following route structure: Work logs -> Work log #
  // NOTE: This must be called before any early returns to satisfy React hooks rules
  const breadcrumbs = useMemo(
    () => [
      {
        label: "Work logs",
        onClick: () => history.push(PATH_WORK_LOGS_PAYEE)
      },
      {
        label: workLog ? `Work log #${workLog.workLogNumber}` : "Work log"
      }
    ],
    [history, workLog]
  );

  // Build toolbar actions based on work log status
  const toolbarActions = useMemo(() => {
    const actions: WSPageToolbarAction[] = [];

    // Submit for review - Primary button for Draft/InProgress
    if (canSubmit) {
      const workItemsCount = queryWorkItems.data?.length || 0;
      actions.push({
        label: "Submit for review",
        buttonKind: "Primary",
        onClick: handleSubmitForReview,
        disabled: workItemsCount === 0 || attachmentsMissing,
        buttonOrder: 1
      });
    }

    // Request editing / Cancel request for editing - shown when work log is in review
    if (canRequestEditing) {
      if (editingRequested) {
        actions.push({
          label: "Cancel request for editing",
          buttonKind: "Secondary",
          onClick: handleCancelEditingRequest,
          buttonOrder: 1
        });
      } else {
        actions.push({
          label: "Request editing",
          buttonKind: "Secondary",
          onClick: handleRequestEditing,
          buttonOrder: 1
        });
      }
    }

    // Menu items (shown in three-dot menu)

    // View invoice - menu item for Approved status
    if (isApproved && workLog?.invoiceId) {
      actions.push({
        label: "View invoice",
        icon: "dollar-circle",
        onClick: handleViewInvoice
      });
    }

    // Delete work log (destructive)
    if (canDelete) {
      actions.push({
        label: "Delete work log",
        icon: "trash",
        onClick: handleDeleteWorkLog,
        destructive: true
      });
    }

    return actions;
  }, [
    isApproved,
    workLog?.invoiceId,
    canSubmit,
    canDelete,
    canRequestEditing,
    editingRequested,
    handleViewInvoice,
    handleSubmitForReview,
    handleDeleteWorkLog,
    handleRequestEditing,
    handleCancelEditingRequest,
    queryWorkItems.data,
    attachmentsMissing
  ]);

  // Error handling - 404 Not Found
  if (queryWorkLog.isError && queryWorkLog.error?.status === 404) {
    return (
      <WSPage>
        <WSEmptyState
          actions={{
            buttons: [
              {
                label: "Back to work logs",
                onClick: () => history.push(PATH_WORK_LOGS_PAYEE)
              }
            ]
          }}
          description="The work log you're looking for doesn't exist or has been deleted."
          title="Work log not found"
          type="search"
        />
      </WSPage>
    );
  }

  // Error handling - 403 Forbidden
  if (queryWorkLog.isError && queryWorkLog.error?.status === 403) {
    return (
      <WSPage>
        <WSElement mt="2XL">
          <WSEmptyState
            actions={{
              buttons: [
                {
                  label: "Back to work logs",
                  onClick: () => history.push(PATH_WORK_LOGS_PAYEE)
                }
              ]
            }}
            description="You don't have permission to view this work log."
            title="Access denied"
            type="error"
          />
        </WSElement>
      </WSPage>
    );
  }

  // Main content
  return (
    <WSPage>
      <WSPageToolbar
        actions={toolbarActions}
        breadcrumbs={breadcrumbs}
        title={workLog ? `Work log #${workLog.workLogNumber}` : "Work log"}
      />

      <AlertWorkLogReviewStatusPayee
        workLog={workLog}
        clientName={payerName}
        onCancelEditingRequest={
          editingRequested ? handleCancelEditingRequest : undefined
        }
        onViewHistory={handleViewHistory}
      />

      <WSGrid gutter="XL" mt="L">
        <WSGrid.Item span={{ m: "8" }}>
          <WSList gap="XL">
            <WorkLogAssignmentInfoPayeeView
              payer={payer}
              payerPayeeEngagement={payerPayeeEngagement}
              shimmer={queryPayerPayeeEngagement.isInitialLoading}
              workLog={workLog}
            />

            <CardWorkItems
              currentUserRole="payee"
              noEdit={!canEdit}
              otherPartyName={payerName}
              payerId={payerId!}
              payerPayeeEngagementId={workLog?.payerPayeeEngagementId!}
              resources={{ payerPayeeEngagement }}
              shimmer={
                queryWorkItems.isInitialLoading ||
                queryWorkDefinitions.isInitialLoading
              }
              workDefinitions={queryWorkDefinitions.data || []}
              workItems={queryWorkItems.data || []}
              workLogId={workLogId}
              onViewHistory={handleViewHistory}
            />
          </WSList>
        </WSGrid.Item>

        <WSGrid.Item span={{ m: "4" }}>
          <WSList gap="XL">
            <WorkLogStatusStepperPayeeView
              shimmer={queryWorkLog.isInitialLoading}
              workLog={workLog}
            />
            <WorkLogAdditionalDetailsEditable
              attachmentsRequired={attachmentsRequired}
              payeeId={userId}
              payerId={payerId}
              shimmer={queryWorkLog.isInitialLoading}
              workLog={workLog}
            />
            <WorkLogHistoryWidget workLogId={workLogId} />
          </WSList>
        </WSGrid.Item>
      </WSGrid>
    </WSPage>
  );
};
